import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaSearch } from 'react-icons/fa';
import styled from 'styled-components';
import theme from '../../../constants/theme';
import { IFormSelectOption } from '../../../interfaces/forms/forms';

interface Props {
	id: string;
	name: string;
	value: string;
	countryCodeName?: string;
	countryCodeValue?: string;
	countries: IFormSelectOption[];
	onBlur: any;
	setFieldValue: any;
	defaultCountryCode?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	autoComplete?: string;
}

const ICELAND_COUNTRY_OPTION: IFormSelectOption = {
	value: 'IS',
	label: 'Ísland',
	telephoneCode: '+354',
};

const Wrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	height: 5rem;
	box-sizing: border-box;
	font-size: inherit;
	line-height: 1.5;
	border: 1px solid ${({ theme }) => theme.colors.borderColor};
	border-radius: 2px;
	background: #fff;

	&:focus-within {
		border-color: ${({ theme }) => theme.colors.primary};
		box-shadow: 0 2px 6px ${({ theme }) => theme.colors.primary}33;
	}
`;

const CountryButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	align-self: stretch;
	gap: 0.8rem;
	width: auto;
	height: 100%;
	padding: 0 1rem 0 2.5rem;
	border: 0;
	border-right: 1px solid ${({ theme }) => theme.colors.borderColor};
	background: transparent;
	cursor: pointer;
	font-family: inherit;
	font-size: inherit;
	line-height: 1;

	svg {
		display: block;
		flex: 0 0 auto;
		width: 1.1rem;
		color: ${({ theme }) => theme.colors.primary};
	}
`;

const Flag = styled.img`
	display: block;
	flex: 0 0 4rem;
	min-width: 4rem;
	width: 4rem;
	height: 3rem;
	border: 1px solid #ccc;
	object-fit: cover;
`;

const Prefix = styled.span`
	display: flex;
	align-items: center;
	align-self: stretch;
	height: 100%;
	padding-left: 2rem;
	padding-right: 1rem;
	font-family: inherit;
	font-size: inherit;
	line-height: 1.5;
	color: ${({ theme }) => theme.colors.textDarker};

	@media (max-width: 1439px) {
		margin-top: 0.1rem;
	}
`;

const Input = styled.input`
	display: flex;
	align-self: stretch;
	align-items: center;
	width: 100%;
	height: 100%;
	padding: 0 2.5rem 0 0;
	font-family: inherit;
	font-size: inherit;
	line-height: 1.5;
	color: ${({ theme }) => theme.colors.textDarker};
	border: 0;
	background: transparent;
	transition: border-color 100ms;

	&:focus,
	&:hover {
		border-color: ${({ theme }) => theme.colors.borderDark};
		outline: none;
	}
`;

const Dropdown = styled.div<{ open: boolean }>`
	position: absolute;
	top: calc(100% + 0.4rem);
	left: 0;
	display: ${({ open }) => (open ? 'block' : 'none')};
	z-index: 10;
	width: 100%;
	max-height: 28rem;
	overflow: hidden;
	border: 1px solid ${({ theme }) => theme.colors.borderColor};
	background: #fff;
	box-shadow: 0 0.8rem 1.8rem rgba(0, 0, 0, 0.12);
`;

const SearchRow = styled.div`
	display: flex;
	align-items: center;
	gap: 1.6rem;
	height: 5rem;
	padding: 0 2rem;
	border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
	color: ${({ theme }) => theme.colors.primary};
`;

const SearchInput = styled.input`
	width: 100%;
	height: 100%;
	border: 0;
	font-size: 1.8rem;
	color: ${({ theme }) => theme.colors.textDarker};
	outline: none;
`;

const Options = styled.div`
	max-height: 23rem;
	overflow-y: auto;
`;

const OptionButton = styled.button<{ active?: boolean }>`
	display: flex;
	align-items: center;
	gap: 1.2rem;
	width: 100%;
	height: 4rem;
	padding: 0 2rem;
	border: 0;
	background: ${({ active }) => (active ? '#efefef' : '#fff')};
	color: ${({ theme }) => theme.colors.textDarker};
	font-size: 1.8rem;
	text-align: left;
	cursor: pointer;

	&:hover {
		background: #f5f5f5;
	}
`;

const normalizeTelephoneCode = (telephoneCode?: string) => {
	if (!telephoneCode) return '';
	const digits = telephoneCode.replace(/\D/g, '').replace(/^00/, '');
	return digits ? `+${digits}` : '';
};

const getTelephoneCodeDigits = (country?: IFormSelectOption) =>
	normalizeTelephoneCode(country?.telephoneCode).replace(/\D/g, '');

const getLocalNumber = (phone: string, telephoneCode: string) => {
	const value = (phone || '').replace(/\s/g, '');
	const codeDigits = telephoneCode.replace(/\D/g, '');

	if (value.startsWith(`+${codeDigits}`)) return value.substring(codeDigits.length + 1);
	if (value.startsWith(`00${codeDigits}`)) return value.substring(codeDigits.length + 2);

	return value.replace(/\D/g, '');
};

const getFlagUrl = (countryCode: string) => `/images/flags-small/${countryCode.toLowerCase()}.png`;

const getCountryCode = (country?: IFormSelectOption) => (country?.value || '').toUpperCase();

const ensureIcelandCountry = (countries: IFormSelectOption[]) => {
	const iceland = countries.find(country => getCountryCode(country) === 'IS');

	if (!iceland) {
		return [ICELAND_COUNTRY_OPTION].concat(countries);
	}

	const icelandWithTelephoneCode = normalizeTelephoneCode(iceland.telephoneCode)
		? iceland
		: { ...iceland, telephoneCode: ICELAND_COUNTRY_OPTION.telephoneCode };

	return [icelandWithTelephoneCode].concat(countries.filter(country => getCountryCode(country) !== 'IS'));
};

const findCountryByTelephoneCode = (digits: string, countries: IFormSelectOption[], exact = false) => {
	const normalizedDigits = digits.replace(/\D/g, '');
	const matches = countries.filter(country => {
		const codeDigits = getTelephoneCodeDigits(country);
		return codeDigits && (exact ? normalizedDigits === codeDigits : normalizedDigits.startsWith(codeDigits));
	});

	return matches.sort((a, b) => getTelephoneCodeDigits(b).length - getTelephoneCodeDigits(a).length)[0] || null;
};

const findCountryFromPhone = (phone: string, countries: IFormSelectOption[]) => {
	const value = (phone || '').replace(/\s/g, '');
	const digitsOnly = value.replace(/\D/g, '');
	if (!digitsOnly || (!value.startsWith('+') && !value.startsWith('00'))) return null;

	const digits = value.startsWith('+') ? value.substring(1) : value.substring(2);
	return findCountryByTelephoneCode(digits, countries);
};

const phoneMatchesCountry = (phone: string, country?: IFormSelectOption) => {
	const value = (phone || '').replace(/\s/g, '');
	const codeDigits = getTelephoneCodeDigits(country);

	return Boolean(
		codeDigits &&
			(value.startsWith(`+${codeDigits}`) || value.startsWith(`00${codeDigits}`))
	);
};

const PhoneCountryInput: FC<Props> = ({
	id,
	name,
	value,
	countryCodeName,
	countryCodeValue,
	countries,
	onBlur,
	setFieldValue,
	defaultCountryCode = 'IS',
	placeholder,
	searchPlaceholder,
	autoComplete,
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const wrapperRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const normalizedDefaultCountryCode = (defaultCountryCode || 'IS').toUpperCase();
	const [selectedCountryCode, setSelectedCountryCode] = useState(
		(countryCodeValue || normalizedDefaultCountryCode).toUpperCase()
	);

	const countriesWithTelephoneCode = useMemo(() => {
		const countriesWithIceland = ensureIcelandCountry(countries || []);
		return countriesWithIceland.filter(country => normalizeTelephoneCode(country.telephoneCode));
	}, [countries]);
	const defaultCountry =
		countriesWithTelephoneCode.find(country => getCountryCode(country) === normalizedDefaultCountryCode) ||
		countriesWithTelephoneCode.find(country => getCountryCode(country) === 'IS') ||
		ICELAND_COUNTRY_OPTION ||
		countriesWithTelephoneCode[0];
	const hasEnteredNumber = Boolean((value || '').replace(/\D/g, ''));
	const selectedCountryByCode = countriesWithTelephoneCode.find(
		country => getCountryCode(country) === selectedCountryCode
	);
	const countryFromPhone = hasEnteredNumber ? findCountryFromPhone(value, countriesWithTelephoneCode) : null;

	const selectedCountry =
		(hasEnteredNumber && phoneMatchesCountry(value, selectedCountryByCode) && selectedCountryByCode) ||
		countryFromPhone ||
		selectedCountryByCode ||
		defaultCountry;

	const telephoneCode = normalizeTelephoneCode(selectedCountry?.telephoneCode);
	const localNumber = getLocalNumber(value, telephoneCode);
	const query = search.trim().toLowerCase();
	const filteredCountries = countriesWithTelephoneCode.filter(country => {
		const code = normalizeTelephoneCode(country.telephoneCode);
		return (
			country.label.toLowerCase().includes(query) ||
			country.value.toLowerCase().includes(query) ||
			code.includes(query)
		);
	});

	const updatePhone = (country: IFormSelectOption, localValue: string) => {
		const code = normalizeTelephoneCode(country.telephoneCode);
		const digits = localValue.replace(/\D/g, '');
		setFieldValue(name, digits ? `${code}${digits}` : '');
	};

	const handlePhoneChange = (rawValue: string) => {
		updatePhone(selectedCountry, rawValue);
	};

	const selectCountry = (country: IFormSelectOption) => {
		const countryCode = getCountryCode(country);
		setSelectedCountryCode(countryCode);
		if (countryCodeName) setFieldValue(countryCodeName, countryCode);
		updatePhone(country, localNumber);
		setOpen(false);
		setSearch('');
	};

	const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, country: IFormSelectOption) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;

		event.preventDefault();
		selectCountry(country);
	};

	useEffect(() => {
		if (open) {
			searchInputRef.current?.focus();
		}
	}, [open]);

	useEffect(() => {
		if (countryCodeValue) setSelectedCountryCode(countryCodeValue.toUpperCase());
	}, [countryCodeValue]);

	useEffect(() => {
		if (!open) return undefined;

		const handleDocumentMouseDown = (event: MouseEvent) => {
			if (wrapperRef.current?.contains(event.target as Node)) return;

			setOpen(false);
			setSearch('');
		};

		document.addEventListener('mousedown', handleDocumentMouseDown);

		return () => {
			document.removeEventListener('mousedown', handleDocumentMouseDown);
		};
	}, [name, onBlur, open]);

	if (!selectedCountry) {
		return (
			<Input
				id={id}
				name={name}
				type="tel"
				value={value || ''}
				placeholder={placeholder}
				autoComplete={autoComplete}
				onBlur={() => onBlur(name, true)}
				onChange={event => setFieldValue(name, event.currentTarget.value)}
			/>
		);
	}

	return (
		<Wrapper ref={wrapperRef}>
			<CountryButton type="button" onClick={() => setOpen(!open)} aria-label="select-phone-country-code">
				<Flag src={getFlagUrl(selectedCountry.value)} alt={`${selectedCountry.label} flag`} />
				<FaChevronDown />
			</CountryButton>
			<Prefix>{telephoneCode}</Prefix>
			<Input
				id={id}
				name={name}
				type="tel"
				value={localNumber}
				placeholder={placeholder}
				autoComplete={autoComplete}
				onBlur={() => onBlur(name, true)}
				onChange={event => handlePhoneChange(event.currentTarget.value)}
			/>
			<Dropdown open={open} aria-hidden={!open}>
				<SearchRow>
					<FaSearch color={theme.colors.primary} />
					<SearchInput
						ref={searchInputRef}
						type="text"
						value={search}
						placeholder={searchPlaceholder}
						onChange={event => setSearch(event.currentTarget.value)}
						tabIndex={open ? 0 : -1}
					/>
				</SearchRow>
				<Options>
					{filteredCountries.map(country => {
						const code = normalizeTelephoneCode(country.telephoneCode);
						return (
							<OptionButton
								key={`${country.value}-${code}`}
								type="button"
								active={country.value === selectedCountry.value}
								onMouseDown={event => event.preventDefault()}
								onClick={() => selectCountry(country)}
								onKeyDown={event => handleOptionKeyDown(event, country)}
								tabIndex={open ? 0 : -1}>
								<Flag src={getFlagUrl(country.value)} alt={`${country.label} flag`} />
								<span>
									{country.label} ({code})
								</span>
							</OptionButton>
						);
					})}
				</Options>
			</Dropdown>
		</Wrapper>
	);
};

export default PhoneCountryInput;
